import { useEffect, useState } from "react";
import { useGroups } from "@/context/GroupContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import {
  Feather,
  Plus,
  KeyRound,
  CheckCircle2,
  Languages,
  Star,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { WelcomeBackBanner } from "@/components/WelcomeBackBanner";
import { DomainNoticeBanner } from "@/components/DomainNoticeBanner";
import { ShareWelcomeCard } from "@/components/ShareWelcomeCard";
import { ReferralCard } from "@/components/ReferralCard";

const PLANS_TEASER = [];
void PLANS_TEASER;

export default function GroupsLandingPage() {
  const { groups, createGroup, joinGroup, error } = useGroups();
  const { t, lang, changeLanguage } = useI18n();
  const navigate = useNavigate();
  const [tab, setTab] = useState("create"); // create | join
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState({});

  useEffect(() => {
    api.get("/reviews/featured").then(({ data }) => setFeatured(data.reviews || [])).catch(() => {});
    api.get("/groups/stats").then(({ data }) => setStats(data.stats || {})).catch(() => {});
  }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    const g = await createGroup(name.trim());
    setLoading(false);
    if (g) {
      toast.success(`Group "${g.name}" created`);
      navigate("/couples", { replace: true });
    }
  };

  const onJoin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const g = await joinGroup(code.trim().toUpperCase());
    setLoading(false);
    if (g) {
      toast.success(`Joined "${g.name}"`);
      navigate("/couples", { replace: true });
    }
  };

  const hasGroups = (groups || []).length > 0;
  const year = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <DomainNoticeBanner />
      <WelcomeBackBanner />
      {/* Top bar with language toggle */}
      <div className="px-4 sm:px-8 pt-4 flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => changeLanguage(lang === "ar" ? "en" : "ar")}
          className="border-olive/40 text-olive hover:bg-olive/10 text-xs font-semibold"
          data-testid="landing-language-toggle"
        >
          <Languages className="w-3.5 h-3.5 mr-1" />
          {lang === "ar" ? "English" : "العربية"}
        </Button>
      </div>
      <main className="flex-1 grid place-items-center p-4 sm:p-8">
        <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-6 items-start" data-testid="groups-landing">

          {/* LEFT column — create/join + subscription teaser */}
          <div className="space-y-6">

          {/* Create / join card */}
          <section
            className="bg-white rounded-2xl border border-app-border shadow-sm p-6 sm:p-10"
            data-testid="create-join-card"
          >
            {hasGroups && (
              <div className="flex items-center gap-2 text-olive mb-3">
                <Feather className="w-5 h-5" />
                <span className="text-xs font-semibold uppercase tracking-[0.3em]">{t("app.brand")}</span>
              </div>
            )}
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-espresso">
              {hasGroups ? t("groups.landing_add") : t("groups.landing_first")}
            </h1>
            <p className="text-sm text-espresso/70 mt-2">{t("groups.landing_sub")}</p>

            <div className="mt-6 flex gap-2 bg-cream-2 p-1 rounded-lg w-fit">
              <button
                onClick={() => setTab("create")}
                className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${tab === "create" ? "bg-white text-espresso shadow-sm" : "text-espresso/60"}`}
                data-testid="tab-create-group"
              >
                <Plus className="w-3.5 h-3.5 inline -mt-0.5 mr-1" /> {t("groups.create")}
              </button>
              <button
                onClick={() => setTab("join")}
                className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${tab === "join" ? "bg-white text-espresso shadow-sm" : "text-espresso/60"}`}
                data-testid="tab-join-group"
              >
                <KeyRound className="w-3.5 h-3.5 inline -mt-0.5 mr-1" /> {t("groups.join")}
              </button>
            </div>

            {tab === "create" ? (
              <form onSubmit={onCreate} className="mt-6 space-y-4" data-testid="create-group-form">
                <div>
                  <Label htmlFor="gname">{t("groups.name_label")}</Label>
                  <Input
                    id="gname"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder={t("groups.name_placeholder")}
                    className="mt-1.5"
                    data-testid="group-name-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || !name.trim()}
                  className="w-full bg-olive hover:bg-olive-hover text-white h-11"
                  data-testid="create-group-submit"
                >
                  {t("groups.create_btn")}
                </Button>
              </form>
            ) : (
              <form onSubmit={onJoin} className="mt-6 space-y-4" data-testid="join-group-form">
                <div>
                  <Label htmlFor="gcode">{t("groups.code_label")}</Label>
                  <Input
                    id="gcode"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    required
                    placeholder="ABCD1234"
                    className="mt-1.5 font-mono tracking-widest uppercase"
                    data-testid="group-code-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || !code.trim()}
                  className="w-full bg-olive hover:bg-olive-hover text-white h-11"
                  data-testid="join-group-submit"
                >
                  {t("groups.join_btn")}
                </Button>
              </form>
            )}

            {error && (
              <div
                className="mt-4 text-sm text-danger bg-danger/10 border border-danger/20 rounded-lg px-3 py-2"
                data-testid="group-error"
              >
                {error}
              </div>
            )}

            {hasGroups && (
              <div className="mt-8 border-t border-app-border pt-6">
                <div className="text-xs font-bold uppercase tracking-widest text-olive mb-3">
                  {t("groups.your_groups")}
                </div>
                <ul className="space-y-2">
                  {groups.map((g) => {
                    const s = stats[g.id];
                    return (
                    <li
                      key={g.id}
                      className="bg-cream rounded-lg px-3 py-2 text-sm"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-espresso">{g.name}</div>
                          <div className="font-mono text-xs text-espresso/60">
                            {t("groups.code_label")}:{" "}
                            <span className="font-bold text-olive">{g.invite_code}</span> · {g.member_count}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate("/couples")}
                          data-testid={`open-group-${g.id}`}
                        >
                          {t("groups.open")}
                        </Button>
                      </div>
                      {s && (
                        <div className="mt-2 pt-2 border-t border-espresso/10 grid grid-cols-4 gap-1 text-center" data-testid={`season-stats-${g.id}`}>
                          <div>
                            <div className="text-sm font-bold text-espresso">{s.eggs}</div>
                            <div className="text-[10px] uppercase tracking-wide text-espresso/50">{t("stats.eggs")}</div>
                          </div>
                          <div>
                            <div className="text-sm font-bold text-espresso">{s.hatched}</div>
                            <div className="text-[10px] uppercase tracking-wide text-espresso/50">{t("stats.hatched")}</div>
                          </div>
                          <div>
                            <div className="text-sm font-bold text-olive">{s.hatch_rate}%</div>
                            <div className="text-[10px] uppercase tracking-wide text-espresso/50">{t("stats.rate")}</div>
                          </div>
                          <div>
                            <div className="text-sm font-bold text-espresso">{s.active_pairs}</div>
                            <div className="text-[10px] uppercase tracking-wide text-espresso/50">{t("stats.pairs")}</div>
                          </div>
                        </div>
                      )}
                    </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </section>

          </div>

          {/* RIGHT column — intro hero (always visible) */}
          <section
            className="bg-gradient-to-br from-olive/8 via-white to-cream-2 rounded-2xl border border-olive/20 shadow-sm p-6 sm:p-8"
            data-testid="landing-intro"
          >
            <div className="flex items-center justify-between gap-2 text-olive mb-3">
              <div className="flex items-center gap-2">
                <Feather className="w-5 h-5" />
                <span className="text-xs font-semibold uppercase tracking-[0.3em]">{t("app.brand")}</span>
              </div>
              <ShareWelcomeCard />
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-espresso">
              {t("groups.intro_title")}
            </h2>
            <p className="text-sm text-espresso/75 mt-2 leading-relaxed">
              {t("groups.intro_body")}
            </p>
            <ul className="mt-4 grid sm:grid-cols-1 gap-2">
              {["groups.intro_pt_1", "groups.intro_pt_2", "groups.intro_pt_3", "groups.intro_pt_4", "groups.intro_pt_5", "groups.intro_pt_7", "groups.intro_pt_8", "groups.intro_pt_6", "groups.intro_pt_9", "groups.intro_pt_10", "groups.intro_pt_11", "groups.intro_pt_12"].map((k) => (
                <li key={k} className="flex items-start gap-2 text-sm text-espresso">
                  <CheckCircle2 className="w-4 h-4 text-olive mt-0.5 flex-shrink-0" />
                  <span>{t(k)}</span>
                </li>
              ))}
              <li className="flex items-start gap-2 text-sm text-espresso" data-testid="intro-app-link">
                <CheckCircle2 className="w-4 h-4 text-olive mt-0.5 flex-shrink-0" />
                <span>
                  {t("groups.intro_pt_13")}{" "}
                  <a href="https://breedtrackers.com" target="_blank" rel="noreferrer" className="font-semibold text-olive underline underline-offset-2 hover:text-olive/80" dir="ltr">https://breedtrackers.com</a>
                </span>
              </li>
            </ul>

            {featured.length > 0 && (
              <div className="mt-6 pt-5 border-t border-olive/15" data-testid="featured-reviews">
                <h3 className="text-sm font-semibold text-espresso uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                  {t("groups.featured_reviews_title")}
                </h3>
                <div className="space-y-3">
                  {featured.slice(0, 4).map((r, i) => (
                    <div
                      key={i}
                      className="bg-white/70 rounded-xl border border-olive/15 p-3"
                      data-testid={`featured-review-${i}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-espresso">{r.user_name}</span>
                        <div className="flex gap-0.5" dir="ltr">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <Star key={n} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                          ))}
                        </div>
                      </div>
                      <p className="text-xs text-espresso/75 mt-1 leading-relaxed">{r.message}</p>
                      {r.admin_reply && (
                        <div className="mt-2 pl-2.5 rtl:pl-0 rtl:pr-2.5 border-l-2 rtl:border-l-0 rtl:border-r-2 border-olive/40">
                          <div className="flex items-center gap-1 text-olive">
                            <Feather className="w-3 h-3" />
                            <span className="text-[9px] font-bold uppercase tracking-wide">{t("reviews.team_badge")}</span>
                          </div>
                          <p className="text-[11px] text-espresso/70 mt-0.5 leading-relaxed">{r.admin_reply}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>
        <div className="max-w-3xl mx-auto mt-8 px-0">
          <ReferralCard compact />
        </div>
      </main>

      {/* Footer with copyright */}
      <footer
        className="border-t border-app-border bg-white/60 px-4 py-4 text-center"
        data-testid="landing-footer"
      >
        <div className="text-xs text-espresso/70">
          {t("footer.copyright").replace("{year}", year)}
        </div>
        <div className="text-[11px] text-espresso/50 mt-0.5">{t("footer.tagline")}</div>
      </footer>
    </div>
  );
}
